import React, { useEffect, useState } from "react";

/**
 * GlitchImageLogo
 * - Always glitches on mount (no session gating)
 */
export default function GlitchImageLogo({
  src,
  alt = "",
  className = "w-32 h-auto",
  duration = 1600,
  respectReduceMotion = true,
  blend = true,
}) {
  // define reduceMotion first
  const reduceMotion =
    respectReduceMotion &&
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // start glitching unless reduced motion is requested
  const [glitch, setGlitch] = useState(!reduceMotion);

  useEffect(() => {
    if (!glitch) return;
    const t = setTimeout(() => setGlitch(false), duration);
    return () => clearTimeout(t);
  }, [glitch, duration]);

  return (
    <div className={`relative inline-block ${className}`} style={{ lineHeight: 0 }}>
      {/* Base (clean) logo */}
      <img src={src} alt={alt} className="relative block w-full h-auto select-none" draggable={false} />

      {/* Glitch layers (only while glitching) */}
      {glitch && (
        <>
          <img
            aria-hidden
            src={src}
            className={`pointer-events-none absolute inset-0 w-full h-full ${
              blend ? "mix-blend-screen" : ""
            } opacity-90`}
            style={{
              filter: "drop-shadow(0 0 10px rgba(212,255,0,.45)) saturate(1.25) contrast(1.05)",
              animation: "glitchShift 0.8s steps(6,end) infinite, glitchJitter 0.22s steps(2,end) infinite",
              clipPath: "polygon(0% 3%,100% 0%,100% 48%,0% 51%)",
            }}
            draggable={false}
          />
          <img
            aria-hidden
            src={src}
            className={`pointer-events-none absolute inset-0 w-full h-full ${
              blend ? "mix-blend-screen" : ""
            } opacity-80`}
            style={{
              filter: "drop-shadow(0 0 8px rgba(255,255,255,.30)) saturate(1.1) contrast(1.05)",
              animation: "glitchShift2 1.05s steps(6,end) infinite, glitchJitter 0.24s steps(2,end) infinite",
              clipPath: "polygon(0% 55%,100% 52%,100% 100%,0% 98%)",
            }}
            draggable={false}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-1 mix-blend-soft-light"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(255,255,255,.05), rgba(255,255,255,.05) 1px, transparent 1px, transparent 3px)",
              animation: "glitchFade 1.6s ease forwards",
            }}
          />
        </>
      )}

      {/* Local keyframes */}
      <style>{`
        @keyframes glitchShift {
          0% { transform: translate(0,0) skewX(0deg); }
          20% { transform: translate(-2px,-1px) skewX(2deg); }
          40% { transform: translate(2px,1px) skewX(-2deg); }
          60% { transform: translate(-1px,1px) skewX(1deg); }
          80% { transform: translate(1px,-1px) skewX(-1deg); }
          100% { transform: translate(0,0) skewX(0deg); }
        }
        @keyframes glitchShift2 {
          0% { transform: translate(0,0) skewX(0deg); }
          20% { transform: translate(2px,1px) skewX(-2deg); }
          40% { transform: translate(-2px,-1px) skewX(2deg); }
          60% { transform: translate(1px,-1px) skewX(-1deg); }
          80% { transform: translate(-1px,1px) skewX(1deg); }
          100% { transform: translate(0,0) skewX(0deg); }
        }
        @keyframes glitchJitter {
          0%, 100% { clip-path: inset(0 0 0 0); }
          50% { clip-path: inset(2% 0 3% 0); }
        }
        @keyframes glitchFade {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
